import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bookmark, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DisputeCard from "@/components/DisputeCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const STATUS_OPTS = [
  ["all", "All statuses"],
  ["complaint_filed", "Complaint Filed"],
  ["response_pending", "Response Pending"],
  ["under_discussion", "Under Discussion"],
  ["resolution_proposed", "Resolution Proposed"],
  ["resolved_by_agreement", "Resolved by Agreement"],
  ["unresolved", "Unresolved"],
  ["no_response", "No Response"],
];

export default function PublicFeed() {
  const { user, openAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [city, setCity] = useState("all");
  const [status, setStatus] = useState("all");
  const [saved, setSaved] = useState([]);

  useEffect(() => { api.get("/complaints/public").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const loadSaved = () => { if (user) api.get("/saved-searches").then((r) => setSaved(r.data)).catch(() => {}); };
  useEffect(() => { loadSaved(); }, [user]);

  const cats = useMemo(() => Array.from(new Set(items.flatMap((i) => i.categories || []))).sort(), [items]);
  const cities = useMemo(() => Array.from(new Set(items.map((i) => i.city).filter(Boolean))).sort(), [items]);

  const filtered = items.filter((i) => {
    if (cat !== "all" && !(i.categories || []).includes(cat)) return false;
    if (city !== "all" && i.city !== city) return false;
    if (status !== "all" && i.status !== status) return false;
    if (q && !(i.summary?.toLowerCase().includes(q.toLowerCase()) || (i.categories || []).join(" ").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const onChallenge = () => (user ? toast.info("Open the complaint from your dashboard to respond or challenge.") : openAuth("login"));

  const saveSearch = async () => {
    if (!user) return openAuth("login");
    const payload = { category: cat !== "all" ? cat : null, city: city !== "all" ? city : null, status: status !== "all" ? status : null };
    if (!payload.category && !payload.city && !payload.status) return toast.error("Pick a category, city or status to save");
    try {
      await api.post("/saved-searches", { ...payload, label: [payload.category, payload.city, STATUS_OPTS.find((s) => s[0] === payload.status)?.[1]].filter(Boolean).join(" · ") });
      toast.success("Search saved — you'll be alerted when new disputes match");
      loadSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const applySaved = (s) => { setCat(s.category || "all"); setCity(s.city || "all"); setStatus(s.status || "all"); };
  const deleteSaved = async (id) => { try { await api.delete(`/saved-searches/${id}`); loadSaved(); } catch { toast.error("Failed"); } };

  return (
    <div className="ca-container py-12">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Community Dispute Feed</h1>
      <p className="text-slate-600 mt-2 max-w-2xl">Anonymised, moderated records. Full mobile numbers, addresses, identity documents and private evidence are never shown. Complaints are allegations until verified or mutually resolved.</p>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input placeholder="Search keyword…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="feed-filter-input" />
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="feed-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="max-h-64"><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger data-testid="feed-filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent className="max-h-64"><SelectItem value="all">All cities</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="feed-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>{STATUS_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-slate-400">{filtered.length} of {items.length} disputes</p>
        <Button variant="outline" size="sm" onClick={saveSearch} data-testid="feed-save-search"><Bookmark className="h-4 w-4 mr-1" /> Save this search</Button>
      </div>

      {user && saved.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-slate-600 mb-2">Your saved searches (you'll be alerted when new disputes match)</p>
          <div className="flex flex-wrap gap-2">
            {saved.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full bg-teal-brand/10 text-teal px-3 py-1 text-xs font-medium" data-testid={`saved-search-${s.id}`}>
                <button onClick={() => applySaved(s)} className="hover:underline">{s.label || "Saved search"}</button>
                <button onClick={() => deleteSaved(s.id)} data-testid={`del-saved-${s.id}`}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {filtered.map((c) => <DisputeCard key={c.id} c={c} onChallenge={onChallenge} />)}
      </div>
    </div>
  );
}
